/**
 * Пакеты гигабайт для сервера «Белый список» (CDN).
 *
 * Сервер работает там, где прямые зарубежные подключения режут, но каждый
 * его гигабайт стоит денег. В тариф входит объём на каждый оплаченный месяц,
 * сверх него — пакеты. Купленное лежит на балансе аккаунта: тратится после
 * гигабайт тарифа, не сгорает и переходит на следующую подписку.
 *
 * Оплата — СБП или картой (цены пакетов в рублях), через тот же счёт Lava,
 * что и тарифы.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useCdnPacks, usePaymentMethods } from '@/entities/subscription/queries';
import { usePayLava, type LavaMethod } from '@/features/payment/usePayLava';
import type { CdnPack } from '@/shared/api/schemas';
import { formatBytes } from '@/shared/lib/format';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Notice, Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Card, GroupCaption } from '@/shared/ui/kit/Card';
import { Icon, type IconName } from '@/shared/ui/kit/Icon';
import { Sheet } from '@/shared/ui/kit/Sheet';

import styles from '../plans/PlansPage.module.css';

const METHODS: readonly { id: LavaMethod; title: string; icon: IconName }[] = [
  { id: 'sbp', title: 'СБП', icon: 'sbp' },
  { id: 'card', title: 'Карта', icon: 'card' },
];

function rubPrice(pack: CdnPack) {
  return pack.prices.find((p) => p.currency === 'RUB');
}

export function PacksPage() {
  const navigate = useNavigate();
  const packs = useCdnPacks();
  const methods = usePaymentMethods();
  const lava = usePayLava();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<CdnPack | null>(null);
  const [methodId, setMethodId] = useState<LavaMethod>('sbp');
  const [paid, setPaid] = useState<CdnPack | null>(null);
  // Экран открывается из подписки; «назад» сначала закрывает шторку.
  useBackButton(() => {
    if (chosen !== null) setChosen(null);
    else if (paid !== null) setPaid(null);
    else void navigate(-1);
  });

  const available = (id: LavaMethod) => Boolean(methods.data?.[id]);
  const price = chosen ? rubPrice(chosen) : undefined;
  const canPay = Boolean(chosen && price && available(methodId));
  const planCode = packs.data?.plan_code ?? null;

  const open = (pack: CdnPack) => {
    lava.reset();
    setMethodId(METHODS.find((m) => available(m.id))?.id ?? 'sbp');
    setChosen(pack);
  };

  const startPayment = () => {
    if (!chosen || !canPay || !planCode) return;
    const pack = chosen;
    void lava
      .pay({ plan_code: planCode, period_code: pack.code, extend_subscription_id: null }, methodId)
      .then(async (ok) => {
        if (!ok) return;
        await queryClient.invalidateQueries({ queryKey: ['cdn-packs'] });
        setChosen(null);
        setPaid(pack);
      });
  };

  if (!packs.isLoading && !packs.error && packs.data && packs.data.gb_per_month === null) {
    return (
      <Notice
        header="Ограничений нет"
        description="Трафик сервера «Белый список» сейчас не ограничен — докупать ничего не нужно."
      />
    );
  }

  return (
    <Screen isLoading={packs.isLoading} error={packs.error} onRetry={() => void packs.refetch()}>
      <h1 className={styles.head}>Белый список</h1>

      <Card padded>
        <p className={styles.note} style={{ margin: 0 }}>
          Сервер «Белый список» работает, когда прямые подключения к зарубежным серверам режут
          (например, в режиме белых списков у мобильного оператора). Его трафик платный, поэтому
          в подписку входит {packs.data?.gb_per_month ?? 25} ГБ на каждый оплаченный месяц.
          Остальные серверы работают без ограничений.
        </p>
      </Card>

      <GroupCaption>Купленные гигабайты</GroupCaption>
      <Card padded>
        <div className={styles.name}>
          Баланс:{' '}
          {packs.data
            ? packs.data.balance_bytes > 0
              ? formatBytes(packs.data.balance_bytes)
              : '0 ГБ'
            : '—'}
        </div>
        <p className={styles.note} style={{ margin: '6px 0 0' }}>
          Тратятся после гигабайт из тарифа, не сгорают и переходят на следующую подписку.
          Работают, пока у вас есть действующая подписка.
        </p>
      </Card>

      <GroupCaption>Докупить</GroupCaption>
      {packs.data?.packs.map((pack) => {
        const rub = rubPrice(pack);
        return (
          <button key={pack.code} type="button" className={styles.period} onClick={() => open(pack)}>
            <span className={styles.body}>
              <span className={styles.name}>{pack.gb} ГБ</span>
              <span className={styles.monthly}>для сервера «Белый список»</span>
            </span>
            <span className={styles.right}>
              <span className={styles.price}>{rub?.formatted ?? '—'}</span>
            </span>
          </button>
        );
      })}

      <Sheet
        open={chosen !== null}
        title={chosen ? `${chosen.gb} ГБ для «Белого списка»` : ''}
        onClose={() => setChosen(null)}
        fit
      >
        <p className={styles.caption}>Способ оплаты</p>
        <div className={styles.methods} role="radiogroup" aria-label="Способ оплаты">
          {METHODS.filter((m) => m.id !== 'card' || available(m.id)).map((m) => {
            const enabled = available(m.id) && price !== undefined;
            const selected = enabled && m.id === methodId;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={!enabled}
                className={[styles.method, selected ? styles.selected : ''].filter(Boolean).join(' ')}
                onClick={() => setMethodId(m.id)}
              >
                <Icon name={m.icon} size={22} />
                <span className={styles.methodName}>{m.title}</span>
                <span className={styles.methodPrice}>
                  {available(m.id) ? (price?.formatted ?? '—') : 'скоро'}
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.pay}>
          <Button block disabled={!canPay || lava.phase !== 'idle'} onClick={startPayment}>
            {lava.phase === 'creating'
              ? 'Выставляем счёт…'
              : lava.phase === 'waiting'
                ? 'Ожидаем оплату…'
                : canPay && price
                  ? `Оплатить ${price.formatted}`
                  : 'Оплата недоступна'}
          </Button>
        </div>
        {lava.phase === 'waiting' ? (
          <div className={styles.waiting}>
            <p className={styles.payNote}>
              Оплатите на открывшейся странице — гигабайты зачислятся автоматически, а бот пришлёт
              сообщение.
            </p>
            <div className={styles.waitingActions}>
              <Button size="s" variant="quiet" onClick={() => lava.reopen()}>
                Открыть оплату снова
              </Button>
              <Button size="s" variant="quiet" onClick={() => lava.cancel()}>
                Отменить
              </Button>
            </div>
          </div>
        ) : lava.error ? (
          <p className={`${styles.payNote} ${styles.payError}`} role="alert">
            {lava.error}
          </p>
        ) : (
          <p className={styles.payNote}>
            Гигабайты не сгорают и переходят на следующую подписку
          </p>
        )}
      </Sheet>

      <Sheet open={paid !== null} title="Оплата прошла" onClose={() => setPaid(null)} fit>
        <p className={styles.payNote}>
          Зачислено {paid?.gb} ГБ для сервера «Белый список». Если сервер был отключён, он
          заработает в течение минуты — обновите подписку в приложении VPN.
        </p>
        <div className={styles.pay}>
          <Button block onClick={() => setPaid(null)}>
            Готово
          </Button>
        </div>
      </Sheet>
    </Screen>
  );
}
